import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Truck, Users, Package, Edit, Trash2, X, Loader2, FileDown, FileSpreadsheet } from "lucide-react";
import LiveSearch, { matchesQuery } from "@/components/LiveSearch";

const TIPO_LABELS = {
  AVERIA: "Avería",
  SERVICIO_PUNTUAL: "Servicio Puntual",
  OBRA: "Obra",
  MANTENIMIENTO: "Mantenimiento",
  VARIOS: "Varios",
};

const DEPARTAMENTOS = ["ADMIN", "CLIMA", "CONS", "EST", "ETEL", "FONT/PCI", "FV", "PDS"];

export function LogsList() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "sadmin";
  const [logs, setLogs] = useState([]);
  const [tipoFilter, setTipoFilter] = useState("");
  const [deptoFilter, setDeptoFilter] = useState("");
  const [empleadoFilter, setEmpleadoFilter] = useState("");
  const [query, setQuery] = useState("");
  const [mineOnly, setMineOnly] = useState(false);
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);
  const [empleados, setEmpleados] = useState([]);

  // Load employee directory for the admin filter
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users/directory").then((r) => {
      setEmpleados((r.data || []).map((u) => ({ id: u.id, name: u.name || u.email })));
    }).catch(() => {});
  }, [isAdmin]);

  const buildParams = () => ({
    limit: 200,
    tipo_servicio: tipoFilter || undefined,
    departamento: deptoFilter || undefined,
    empleado_id: (isAdmin && empleadoFilter) ? empleadoFilter : undefined,
    fecha_desde: fechaDesde || undefined,
    fecha_hasta: fechaHasta || undefined,
    mine_only: mineOnly || undefined,
  });

  const clearFilters = () => {
    setTipoFilter("");
    setDeptoFilter("");
    setEmpleadoFilter("");
    setQuery("");
    setFechaDesde("");
    setFechaHasta("");
    setMineOnly(false);
    // load with cleared filters on next tick
    setTimeout(() => load(), 0);
  };

  // Quick preset: today / this week / this month
  const applyDatePreset = (preset) => {
    const now = new Date();
    // LOCAL date components — toISOString() rolls to UTC and shifts the day.
    const iso = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    if (preset === "today") {
      setFechaDesde(iso(now));
      setFechaHasta(iso(now));
    } else if (preset === "week") {
      const day = (now.getDay() + 6) % 7; // Mon=0
      const monday = new Date(now); monday.setDate(now.getDate() - day);
      const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
      setFechaDesde(iso(monday));
      setFechaHasta(iso(sunday));
    } else if (preset === "month") {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setFechaDesde(iso(first));
      setFechaHasta(iso(last));
    }
    setTimeout(() => load(), 0);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/work-logs", { params: buildParams() });
      setLogs(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // initial

  const downloadXlsx = async () => {
    setDownloadingXlsx(true);
    try {
      const res = await api.get("/work-logs/export.xlsx", {
        params: buildParams(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `partes_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      toast.success("Excel descargado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo descargar Excel");
    } finally {
      setDownloadingXlsx(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="logs-list">
      <header>
        <div className="label-up text-[#FF4500]">Historial</div>
        <h1 className="font-display font-black text-3xl tracking-tight mt-1">Partes registrados</h1>
        <p className="text-zinc-600 text-sm mt-1">
          {isAdmin ? "Vista global de toda la empresa." : "Tus partes recientes."}
        </p>
      </header>

      <LiveSearch
        value={query}
        onChange={setQuery}
        placeholder="Buscar obra, vehículo, operario, tipo…"
        testId="logs-search"
      />

      <div className="brutal-card p-4 space-y-3">
        <div className="grid grid-cols-1 gap-3">
          <select
            data-testid="filter-tipo"
            className="brutal-input"
            value={tipoFilter}
            onChange={(e) => setTipoFilter(e.target.value)}
          >
            <option value="">Todos los tipos</option>
            {Object.entries(TIPO_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <select
            data-testid="filter-departamento"
            className="brutal-input"
            value={deptoFilter}
            onChange={(e) => setDeptoFilter(e.target.value)}
          >
            <option value="">Todos los departamentos</option>
            {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          {isAdmin ? (
            <select
              data-testid="filter-empleado"
              className="brutal-input"
              value={empleadoFilter}
              onChange={(e) => setEmpleadoFilter(e.target.value)}
            >
              <option value="">Todos los empleados</option>
              {empleados.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          ) : (
            <div />
          )}
        </div>

        {/* Date range filter */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-up block mb-1">Desde</label>
            <input
              type="date"
              data-testid="filter-fecha-desde"
              className="brutal-input w-full"
              value={fechaDesde}
              max={fechaHasta || undefined}
              onChange={(e) => setFechaDesde(e.target.value)}
            />
          </div>
          <div>
            <label className="label-up block mb-1">Hasta</label>
            <input
              type="date"
              data-testid="filter-fecha-hasta"
              className="brutal-input w-full"
              value={fechaHasta}
              min={fechaDesde || undefined}
              onChange={(e) => setFechaHasta(e.target.value)}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="label-up text-slate-400">Rápido:</span>
          <button
            type="button"
            data-testid="preset-today"
            onClick={() => applyDatePreset("today")}
            className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            Hoy
          </button>
          <button
            type="button"
            data-testid="preset-week"
            onClick={() => applyDatePreset("week")}
            className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            Semana
          </button>
          <button
            type="button"
            data-testid="preset-month"
            onClick={() => applyDatePreset("month")}
            className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            Mes
          </button>
          <button
            type="button"
            data-testid="clear-filters"
            onClick={clearFilters}
            className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider rounded-md text-rose-700 hover:bg-rose-50 ml-auto"
          >
            Limpiar
          </button>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          {isAdmin && (
            <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
              <input type="checkbox" data-testid="filter-mine" className="w-4 h-4 brutal-border accent-[#FF4500]"
                checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} />
              Solo míos
            </label>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              data-testid="logs-download-xlsx"
              onClick={downloadXlsx}
              disabled={downloadingXlsx}
              title="Descargar Excel con los filtros actuales"
              className="h-10 px-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 rounded-lg border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
            >
              {downloadingXlsx ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
              Excel
            </button>
            <button data-testid="apply-filters" onClick={load} className="brutal-btn-dark h-10 px-4 text-xs">
              Aplicar
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="brutal-card p-6 text-center text-zinc-600">Cargando...</div>
      ) : (() => {
        const visible = logs.filter((l) =>
          matchesQuery(
            query,
            l.nombre_servicio,
            l.tipo_servicio,
            TIPO_LABELS[l.tipo_servicio],
            l.vehiculo,
            l.departamento,
            l.created_by_name,
            l.descripcion_tarea,
            (l.operarios || []).map((o) => o.nombre).join(" "),
          )
        );
        if (visible.length === 0) {
          return (
            <div className="brutal-card p-6 text-center">
              <div className="font-bold">Sin resultados</div>
              <p className="text-sm text-zinc-600 mt-1">
                {query ? `Ningún parte coincide con "${query}".` : "Ajusta los filtros o crea un parte nuevo."}
              </p>
            </div>
          );
        }
        return (
          <>
            {query && (
              <p className="text-xs text-slate-500 -my-1 pl-1" data-testid="logs-count">
                {visible.length} de {logs.length} partes coinciden con «{query}»
              </p>
            )}
            <ul className="space-y-3">
              {visible.map((l) => (
                <li key={l.id} data-testid={`log-${l.id}`}>
                  <Link to={`/logs/${l.id}`} className="brutal-card p-4 flex flex-col gap-2 hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform">
                    <div className="flex items-center justify-between gap-2">
                      <span className="brutal-chip bg-[#FACC15]">{TIPO_LABELS[l.tipo_servicio]}</span>
                      <span className="text-xs text-zinc-500 font-mono">{l.fecha}</span>
                    </div>
                    <div className="font-display font-black text-lg leading-tight">{l.nombre_servicio || "—"}</div>
                    <div className="flex flex-wrap gap-3 text-xs text-zinc-600">
                      <span className="inline-flex items-center gap-1"><Users size={12} /> {l.operarios?.length || 0}</span>
                      <span className="inline-flex items-center gap-1"><Package size={12} /> {l.materiales?.length || 0}</span>
                      <span className="inline-flex items-center gap-1"><Truck size={12} /> {l.vehiculo || "—"}</span>
                      <span className="ml-auto text-zinc-500">{l.created_by_name}</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        );
      })()}
    </div>
  );
}

export function LogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [log, setLog] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [thumbs, setThumbs] = useState({});
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    api.get(`/work-logs/${id}`).then((r) => setLog(r.data)).catch(() => {
      toast.error("Parte no encontrado");
      navigate("/logs");
    });
    api.get(`/work-logs/${id}/photos`).then((r) => setPhotos(r.data || [])).catch(() => {});
  }, [id, navigate]);

  // Load thumbnails as blob URLs
  useEffect(() => {
    let cancelled = false;
    const fetchThumbs = async () => {
      const next = { ...thumbs };
      for (const ph of photos) {
        if (next[ph.id]) continue;
        try {
          const res = await api.get(`/files/${ph.id}`, { responseType: "blob" });
          if (cancelled) return;
          next[ph.id] = URL.createObjectURL(res.data);
        } catch { /* ignore */ }
      }
      if (!cancelled) setThumbs(next);
    };
    if (photos.length) fetchThumbs();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);

  useEffect(() => () => {
    Object.values(thumbs).forEach((u) => URL.revokeObjectURL(u));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDelete = async () => {
    if (!window.confirm("¿Eliminar este parte?")) return;
    try {
      await api.delete(`/work-logs/${id}`);
      toast.success("Eliminado");
      navigate("/logs");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const [downloading, setDownloading] = useState(false);
  const onDownloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/work-logs/${id}/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = (log?.nombre_servicio || "parte").replace(/[^\w-]+/g, "_").slice(0, 40);
      a.download = `parte_${safe}_${log?.fecha || ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch {
      toast.error("No se pudo generar el PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (!log) return <div className="text-center text-zinc-600">Cargando...</div>;

  const canManage = user?.role === "admin" || user?.role === "sadmin";
  const totalHoras = log.operarios?.reduce((a, o) => a + (Number(o.horas) || 0), 0) || 0;

  return (
    <div className="space-y-5" data-testid="log-detail">
      <button onClick={() => navigate("/logs")} className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider">
        <ArrowLeft size={16} /> Volver
      </button>

      <header className="brutal-card p-5">
        <div className="flex items-center justify-between gap-2">
          <span className="brutal-chip bg-[#FACC15]">{TIPO_LABELS[log.tipo_servicio]}</span>
          <span className="brutal-chip">{log.departamento}</span>
        </div>
        <h1 className="font-display font-black text-2xl mt-3 leading-tight">{log.nombre_servicio}</h1>
        <div className="mt-3 flex flex-wrap gap-3 text-sm text-zinc-700">
          <span className="inline-flex items-center gap-1.5"><Calendar size={14} /> {log.fecha}</span>
          <span className="inline-flex items-center gap-1.5"><Truck size={14} /> {log.vehiculo || "—"}</span>
          <span className="inline-flex items-center gap-1.5">Por: <strong>{log.created_by_name}</strong></span>
        </div>
        <div className="flex gap-2 mt-4 flex-wrap">
          {canManage && (
            <Link to={`/edit/${log.id}`} className="brutal-btn-ghost h-10 px-3 text-xs inline-flex items-center gap-2" data-testid="edit-log">
              <Edit size={14} /> Editar
            </Link>
          )}
          <button
            data-testid="download-pdf"
            onClick={onDownloadPdf}
            disabled={downloading}
            className="brutal-btn-ghost h-10 px-3 text-xs inline-flex items-center gap-2 hover:bg-[#0E78C2] hover:text-white disabled:opacity-60"
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
            Descargar PDF
          </button>
          {canManage && (
            <button data-testid="delete-log" onClick={onDelete}
              className="brutal-btn-ghost h-10 px-3 text-xs inline-flex items-center gap-2 hover:bg-[#EF4444] hover:text-white">
              <Trash2 size={14} /> Eliminar
            </button>
          )}
        </div>
        {!canManage && (
          <p className="mt-3 text-[11px] text-slate-500 italic">
            Este parte está bloqueado. Contacta con un administrador si necesitas modificarlo.
          </p>
        )}
      </header>

      <section className="brutal-card p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-xl">Personal</h2>
          <span className="brutal-chip">{totalHoras} h totales</span>
        </div>
        {log.operarios?.length ? (
          <ul className="mt-3 divide-y-2 divide-zinc-200">
            {log.operarios.map((o, i) => (
              <li key={i} className="py-2 flex items-center justify-between">
                <span className="font-medium">{o.nombre}</span>
                <span className="font-mono text-sm">{o.horas} h</span>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-zinc-500 mt-2">Sin operarios.</p>}
      </section>

      <section className="brutal-card p-5">
        <h2 className="font-display font-black text-xl">Material empleado</h2>
        {log.materiales?.length ? (
          <ul className="mt-3 divide-y-2 divide-zinc-200">
            {log.materiales.map((m, i) => (
              <li key={i} className="py-2 flex items-center justify-between gap-3">
                <span className="font-medium flex-1">{m.descripcion}</span>
                <span className="brutal-chip">{m.cantidad || "—"}</span>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-zinc-500 mt-2">Sin materiales.</p>}
      </section>

      <section className="brutal-card p-5">
        <h2 className="font-display font-black text-xl">Tarea realizada</h2>
        <p className="text-sm mt-2 whitespace-pre-wrap">{log.descripcion_tarea || "—"}</p>
        {log.observaciones && (
          <>
            <div className="label-up mt-4">Observaciones</div>
            <p className="text-sm mt-1 whitespace-pre-wrap">{log.observaciones}</p>
          </>
        )}
      </section>

      {/* Photos gallery */}
      <section className="brutal-card p-5" data-testid="log-photos-section">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-xl">Fotos</h2>
          <span className="brutal-chip">{photos.length}</span>
        </div>
        {photos.length === 0 ? (
          <p className="text-sm text-slate-500 mt-3">Este parte no tiene fotos adjuntas.</p>
        ) : (
          <ul className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4">
            {photos.map((ph) => (
              <li
                key={ph.id}
                className="aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-50"
              >
                {thumbs[ph.id] ? (
                  <button
                    type="button"
                    onClick={() => setViewer(thumbs[ph.id])}
                    data-testid={`detail-photo-${ph.id}`}
                    className="w-full h-full"
                  >
                    <img
                      src={thumbs[ph.id]}
                      alt={ph.original_filename}
                      className="w-full h-full object-cover hover:scale-105 transition-transform"
                    />
                  </button>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400">
                    <Loader2 size={20} className="animate-spin" />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {viewer && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setViewer(null)}
          data-testid="detail-photo-viewer"
        >
          <button
            type="button"
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            onClick={() => setViewer(null)}
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
          <img
            src={viewer}
            alt=""
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
